import { Dialog } from "../../../../common/core-ui/Dialog.tsx";
import { FormattedMessage, useIntl } from "react-intl";
import CourseFieldSelect from "./CourseFieldSelect.tsx";
import styles from "./CoursePickerDialog.module.scss";
import { useReducer, useRef, useState } from "react";
import { clsx } from "clsx";
import {
  useCoursePickerFetcher,
  useCampusPickerFetcherWithDefault,
  useTermPickerFetcher,
  defaultCampusId,
} from "./coursePickerLoader.ts";
import MountAfterDelay from "../../../../common/core-ui/MountWithDelay.tsx";
import { useNavigate } from "react-router-dom";
import { useBrandLoaderData } from "../../../root/rootLoader.ts";

type FieldId = "terms" | "departments" | "courses" | "sections";
const orderedFields: FieldId[] = [
  "terms",
  "departments",
  "courses",
  "sections",
];
const baseAction = "/search/api/courses";

function reducer(
  state: string[],
  action: { type: FieldId; value: string } | { type: "reset" }
) {
  if (action.type === "reset") {
    return [];
  }
  const index = orderedFields.indexOf(action.type);
  if (action.value) {
    return [...state.slice(0, index), action.value];
  } else {
    return state.slice(0, index);
  }
}

export default function CoursePickerDialog({
  onRequestClose,
}: {
  onRequestClose: () => void;
}) {
  const intl = useIntl();
  const brand = useBrandLoaderData();
  const hasCampuses = brand.VirtualStore.CourseBrowse.Campus.enabled;
  const [data, dispatch] = useReducer(reducer, []);
  const [hasIncompleteFormError, setHasIncompleteFormError] = useState(false);
  const errorRef = useRef<HTMLDivElement>(null);
  const campusFetcher = useCampusPickerFetcherWithDefault();
  const termsFetcher = useTermPickerFetcher();
  const navigate = useNavigate();
  const [campusId, setCampusId] = useState<string | undefined>(undefined);

  const getCampusRoute = () =>
    campusId && campusId !== defaultCampusId ? `/campus/${campusId}` : "";

  const fields: {
    label: string;
    placeholder: string;
    id: FieldId;
    fetcher?: ReturnType<typeof useCoursePickerFetcher>;
  }[] = [
    {
      label: intl.formatMessage({ defaultMessage: "Term" }),
      placeholder: intl.formatMessage({ defaultMessage: "Select Term" }),
      id: "terms",
      fetcher: termsFetcher,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Department" }),
      placeholder: intl.formatMessage({ defaultMessage: "Select Department" }),
      id: "departments",
    },
    {
      label: intl.formatMessage({ defaultMessage: "Course" }),
      placeholder: intl.formatMessage({ defaultMessage: "Select Course" }),
      id: "courses",
    },
    {
      label: intl.formatMessage({ defaultMessage: "Section" }),
      placeholder: intl.formatMessage({ defaultMessage: "Select Section" }),
      id: "sections",
    },
  ];

  const goToCourseMaterials = () => {
    if (data.length !== fields.length) {
      setHasIncompleteFormError(true);
      window.setTimeout(() => {
        errorRef.current?.focus();
      }, 10);
    } else {
      onRequestClose();
      void navigate(
        `/search/terms/${data[0]}/departments/${data[1]}/courses/${data[2]}/sections/${data[3]}`
      );
    }
  };

  return (
    <Dialog
      darkHeader
      onRequestClose={onRequestClose}
      title={intl.formatMessage({ defaultMessage: "Select Course" })}
      primaryButton={{
        label: intl.formatMessage({ defaultMessage: "View Course Materials" }),
        onClick: goToCourseMaterials,
      }}
      secondaryButton={{
        label: intl.formatMessage({ defaultMessage: "Cancel" }),
        onClick: onRequestClose,
      }}
    >
      <FormattedMessage
        tagName="div"
        defaultMessage="Select any course at your institution to view required course materials."
      />
      {((campusFetcher.data && !campusFetcher.data.ok) ||
        (termsFetcher.data &&
          (!termsFetcher.data.ok || termsFetcher.data.data?.length === 0))) && (
        <div
          className={clsx(
            "vst-validation-box",
            "vst-validation-box-variant-warning",
            styles.error
          )}
          aria-live="polite"
        >
          <MountAfterDelay timeout={10}>
            <FormattedMessage defaultMessage="Course materials information is not available for upcoming terms. Please check back later." />
          </MountAfterDelay>
        </div>
      )}
      {hasIncompleteFormError && (
        <div
          ref={errorRef}
          className={clsx("vst-validation-box", styles.error)}
          tabIndex={-1}
        >
          <FormattedMessage defaultMessage="Please fill out all fields" />
        </div>
      )}

      <div className={styles.selectBlock}>
        {hasCampuses && (
          <CourseFieldSelect
            key={"campus"}
            label={intl.formatMessage({ defaultMessage: "Campus" })}
            placeholder={intl.formatMessage({
              defaultMessage: "Select Campus",
            })}
            onChange={(e) => {
              setCampusId(e.target.value);
              dispatch({ type: "reset" });
              if (
                campusId &&
                campusId !== defaultCampusId &&
                campusId !== e.target.value
              ) {
                // terms are loaded here so key change remount on select won't work
                void termsFetcher.load(
                  `${baseAction}/campus/${e.target.value}`
                );
              }
              setHasIncompleteFormError(false);
            }}
            action={"/search/api/courses/campus"}
            value={campusId}
            fetcher={campusFetcher}
          />
        )}

        {fields.map((field, i) => {
          const action =
            hasCampuses && !campusId
              ? undefined // supports campus change from default to unselected
              : data.length >= i
              ? (
                  baseAction +
                  getCampusRoute() +
                  "/" +
                  data.slice(0, i).join("/")
                ).replace(/\/$/, "")
              : undefined;

          const fetcher =
            field.id === "terms" && hasCampuses && !campusId
              ? undefined
              : field.fetcher;
          return (
            <CourseFieldSelect
              key={action ?? field.id}
              label={field.label}
              placeholder={field.placeholder}
              onChange={(e) => {
                dispatch({ type: field.id, value: e.target.value });
                if (i === fields.length - 1) {
                  setHasIncompleteFormError(false);
                }
              }}
              value={data[i] ?? ""}
              action={action}
              fetcher={fetcher}
            />
          );
        })}
      </div>
    </Dialog>
  );
}
